import { Link } from '@tanstack/react-router'
import { AlertTriangle, Check, CircleOff, FileText, Package, Settings2 } from 'lucide-react'
import { useProductConfigAccessoryV4 } from '@/api/hooks'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatMoney } from '@/lib/utils'
import type { components } from '@/api/schema'

type LinkedProduct = components['schemas']['ConfigAccessoryV4LinkedProductRead']

function LinkedProductValue({
  product,
  fallback,
}: {
  product: LinkedProduct | null | undefined
  fallback: string
}) {
  if (!product) {
    return <span className="font-medium">{fallback}</span>
  }

  const model = (
    <span className="font-medium text-primary">
      {product.model}
      {!product.active && (
        <span className="ml-1 text-xs font-normal text-muted-foreground">(inactive)</span>
      )}
    </span>
  )
  return (
    <div className="min-w-0">
      {product.product_uuid ? (
        <Link
          to="/products/$productId"
          params={{ productId: product.product_uuid }}
          className="break-words hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {model}
        </Link>
      ) : (
        model
      )}
      {product.stock_model && product.stock_model !== product.model && (
        <p className="mt-0.5 break-all text-xs text-muted-foreground">
          Stock model: {product.stock_model}
        </p>
      )}
    </div>
  )
}

function AvailabilityBadge({ availability }: { availability: string }) {
  return availability === 'supported' ? (
    <Badge variant="outline" className="gap-1 font-normal">
      <Check className="size-3" aria-hidden="true" />
      Supported
    </Badge>
  ) : (
    <Badge variant="secondary" className="gap-1 font-normal">
      <CircleOff className="size-3" aria-hidden="true" />
      Unavailable
    </Badge>
  )
}

export function ProductConfigV4Section({ productId }: { productId: string }) {
  const { data, isLoading, isError } = useProductConfigAccessoryV4(productId)

  if (isLoading) {
    return (
      <Card data-config-v4-loading="true" role="status" aria-label="Loading CONFIG V4 evidence">
        <CardHeader>
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-72 max-w-full" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="border-destructive/30" data-config-v4-error="true" role="alert">
        <CardContent className="flex items-start gap-3 pt-0">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden="true" />
          <div>
            <p className="text-sm font-medium">CONFIG V4 evidence could not be loaded</p>
            <p className="mt-1 text-sm text-muted-foreground">
              The stored evidence may be unavailable or may have failed validation. Legacy catalog
              data remains below.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return (
      <Card data-config-v4-empty="true">
        <CardContent className="flex items-start gap-3 pt-0">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Settings2 className="size-4 text-muted-foreground" aria-hidden="true" />
          </div>
          <div>
            <p className="text-sm font-medium">No structured CONFIG V4 evidence</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              This product does not have a current quote-readable CONFIG V4 result.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const optionCount = data.configuration_groups.reduce(
    (total, group) => total + group.options.length,
    0,
  )
  const accessoryCount = data.accessory_groups.reduce(
    (total, group) => total + group.options.length,
    0,
  )
  const statusLabel = data.provenance.result_state === 'shopify_v4_synced' ? 'Published' : 'Ready'

  return (
    <Card data-config-v4="true">
      <CardHeader className="border-b">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold">Raw AI configuration and accessories</h2>
              <Badge variant="default">CONFIG V4</Badge>
              <Badge variant="outline" className="font-normal">
                {statusLabel}
              </Badge>
            </div>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Source-supported choices and linked catalog products used by quoting and storefront
              recommendations.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>{data.configuration_groups.length} groups</span>
            <span>{optionCount} choices</span>
            <span>{accessoryCount} accessories</span>
            <span>{data.alternatives.length} alternatives</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        {data.configuration_groups.map((group) => (
          <section key={group.group_id} aria-labelledby={`config-v4-${group.group_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Settings2 className="size-4 text-muted-foreground" aria-hidden="true" />
              <h3 id={`config-v4-${group.group_id}`} className="text-sm font-semibold">
                {group.name}
              </h3>
              <Badge variant={group.required ? 'default' : 'secondary'} className="font-normal">
                {group.required ? 'Required' : 'Optional'}
              </Badge>
            </div>
            <div className="mt-3 divide-y rounded-lg border">
              {group.options.map((option) => (
                <div
                  key={option.selection_id}
                  className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <LinkedProductValue product={option.linked_product} fallback={option.label} />
                      {option.is_default && <Badge variant="secondary">Default</Badge>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span>{option.label}</span>
                      {option.manufacturer_option_id && (
                        <span className="font-mono">Code {option.manufacturer_option_id}</span>
                      )}
                      {option.product_sku && (
                        <span className="font-mono">SKU {option.product_sku}</span>
                      )}
                      {option.same_sku && <span>Same base SKU</span>}
                    </div>
                    {option.unavailable_reason && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {option.unavailable_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <AvailabilityBadge availability={option.availability} />
                    {option.unit_price != null && (
                      <span className="text-sm font-medium tabular-nums">
                        {formatMoney(option.unit_price)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}

        {data.accessory_groups.length > 0 && (
          <section aria-labelledby="config-v4-accessories">
            <div className="flex items-center gap-2">
              <Package className="size-4 text-muted-foreground" aria-hidden="true" />
              <h3 id="config-v4-accessories" className="text-sm font-semibold">
                Recommended accessories
              </h3>
            </div>
            <div className="mt-3 divide-y rounded-lg border">
              {data.accessory_groups.flatMap((group) =>
                group.options.map((option) => (
                  <div
                    key={option.selection_id}
                    className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                  >
                    <div className="min-w-0">
                      <LinkedProductValue
                        product={option.linked_product}
                        fallback={option.manufacturer_sku}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        {group.name} · <span className="font-mono">{option.manufacturer_sku}</span>
                      </p>
                      {option.unavailable_reason && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {option.unavailable_reason}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                      <AvailabilityBadge availability={option.availability} />
                      {option.unit_price != null && (
                        <span className="text-sm font-medium tabular-nums">
                          {formatMoney(option.unit_price)}
                        </span>
                      )}
                    </div>
                  </div>
                )),
              )}
            </div>
          </section>
        )}

        {data.alternatives.length > 0 && (
          <section aria-labelledby="config-v4-alternatives">
            <h3 id="config-v4-alternatives" className="text-sm font-semibold">
              Documented alternatives
            </h3>
            <div className="mt-3 divide-y rounded-lg border">
              {data.alternatives.map((alternative) => (
                <div
                  key={alternative.selection_id}
                  className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                >
                  <div className="min-w-0">
                    <LinkedProductValue
                      product={alternative.linked_product}
                      fallback={alternative.manufacturer_sku}
                    />
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      {alternative.manufacturer_sku}
                    </p>
                    {alternative.unavailable_reason && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {alternative.unavailable_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <AvailabilityBadge availability={alternative.availability} />
                    {alternative.unit_price != null && (
                      <span className="text-sm font-medium tabular-nums">
                        {formatMoney(alternative.unit_price)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {data.warnings.length > 0 && (
          <details className="rounded-lg border px-4 py-3">
            <summary className="cursor-pointer text-sm font-medium">
              {data.warnings.length} evidence warning
              {data.warnings.length === 1 ? '' : 's'}
            </summary>
            <ul className="mt-3 space-y-2 text-xs text-muted-foreground">
              {data.warnings.map((warning) => (
                <li key={`${warning.code}-${warning.source_path}`}>
                  {warning.message}
                </li>
              ))}
            </ul>
          </details>
        )}

        {data.provenance.documents.length > 0 && (
          <div className="flex items-center gap-1 border-t pt-4 text-xs text-muted-foreground">
            <FileText className="size-3" aria-hidden="true" />
            {data.provenance.documents.length} supporting document
            {data.provenance.documents.length === 1 ? '' : 's'}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
